import { useMutation } from "@apollo/client/react";
import {
  ContentListFragment,
  ExamSyllabusAdminPageData,
  ExamSyllabusAdminPageDataVariables,
  ExamSyllabusAdminPageData_examSyllabusById,
  ExamSyllabusAdminPageData_examSyllabusById_elements,
  ReorderExamSyllabusElementInput,
  ReorderExamSyllabusElementVariables,
} from "__generated__/types";
import { MutationLoadingOrError } from "common/components/forms/MutationLoadingOrError";
import { AdministrationLayout } from "common/components/layouts/AdministrationLayout";
import { LoadingLayout } from "common/components/layouts/LoadingLayout";
import { NoDataError } from "common/components/NoDataError";
import { Select } from "common/components/Select";
import {
  contentStatusFilterValueLabel,
  contentStatusFilterValueToSelectOption,
} from "common/content-status-filter";
import { useSimpleQuery } from "common/graphql/useSimpleQuery";
import { urls } from "common/urls";
import {
  ListElement,
  ListElementStudyProgrammeElement,
} from "domain/admin/common/AdministrationList";
import { CreateContentFooterBox } from "domain/admin/common/CreateContentFooterBox";
import { getListElementType } from "domain/admin/common/utils";
import { ExamSyllabusAddFolder } from "domain/admin/folder/ExamSyllabusAddFolder";
import {
  chainFilters,
  listFilter,
  noDeletedStatusFilter,
} from "domain/admin/util";
import { getParentFromContentContext, isInSyllabus } from "domain/common/utils";
import { ContentStatusFilterValue } from "domain/constants";
import { contentBreadcrumbFragment } from "domain/pages/breadcrumbs";
import gql from "graphql-tag";
import React from "react";
import { useParams } from "react-router";
import { Link } from "react-router-dom";

// Dynamic import to cause code split for react-dnd library.
const AdministrationList = React.lazy(() =>
  import("domain/admin/common/AdministrationList").then((it) => ({
    default: it.AdministrationList,
  })),
);

const GET_EXAM_SYLLABUS = gql`
  query ExamSyllabusAdminPageData($examId: ID!) {
    examSyllabusById(id: $examId) {
      status
      id
      title
      ...ContentBreadcrumbFragment
      elements {
        id
        content {
          id
          status
          title
          ... on Folder {
            type
          }
        }
      }
    }
  }
  ${contentBreadcrumbFragment}
`;

const MUTATION = gql`
  mutation ReorderExamSyllabusElement(
    $input: ReorderExamSyllabusElementInput!
  ) {
    reorderExamSyllabusElement(input: $input) {
      examSyllabus {
        id
      }
    }
  }
`;

const convertToList = (
  data: ExamSyllabusAdminPageData_examSyllabusById_elements[],
  examSyllabus: ExamSyllabusAdminPageData_examSyllabusById,
): ListElement[] => {
  return data.map((it) => ({
    category: "ExamSyllabusElement",
    id: it.id,
    type: getListElementType(it.content),
    status: it.content.status,
    examSyllabusElementId: it.id,
    examSyllabusId: examSyllabus.id,
    title: it.content.title,
    contentId: it.content.id,
  }));
};

type RouteProps = {
  examSyllabusId: string;
};

export const ExamSyllabusAdminPage: React.FC = () => {
  const { examSyllabusId } = useParams<RouteProps>();

  const [contentStatusFilterValue, setContentStatusFilterValue] =
    React.useState<ContentStatusFilterValue>(ContentStatusFilterValue.ALL);

  if (!examSyllabusId) {
    throw new Error("Unexpected error: Missing examSyllabusId");
  }

  const { data, loadingOrError, refetch } = useSimpleQuery<
    ExamSyllabusAdminPageData,
    ExamSyllabusAdminPageDataVariables
  >(GET_EXAM_SYLLABUS, {
    variables: {
      examId: examSyllabusId,
    },
  });

  const [mutate, { error }] = useMutation<
    ReorderExamSyllabusElementInput,
    ReorderExamSyllabusElementVariables
  >(MUTATION);

  if (loadingOrError) {
    return <LoadingLayout>{loadingOrError}</LoadingLayout>;
  }

  if (!data || !data.examSyllabusById) {
    return (
      <LoadingLayout>
        <NoDataError />
      </LoadingLayout>
    );
  }

  const handleSaveListOrder = async (
    id: string,
    moveToAfterElement: ListElementStudyProgrammeElement | null,
  ) => {
    await mutate({
      variables: {
        input: {
          examSyllabusId,
          elementId: id,
          moveToAfterElementId: moveToAfterElement?.id,
        },
      },
    });

    return refetch();
  };

  const filteredElementsList = data.examSyllabusById.elements.filter(
    chainFilters(
      (e) => noDeletedStatusFilter(e.content.status),
      (e) =>
        listFilter(e.content as ContentListFragment, contentStatusFilterValue),
    ),
  );

  const { context, title, elements, id } = data.examSyllabusById;
  const studyProgramme = getParentFromContentContext(context);

  return (
    <AdministrationLayout title={title} context={context ?? []}>
      <Link to={urls.examAdminEdit(id)}>Rediger pensumdetaljer</Link>
      <br />
      <Link to={urls.exam(id)}>Studentvisning</Link>
      <div>
        <Select
          name="status"
          options={Object.values(ContentStatusFilterValue).map(
            contentStatusFilterValueToSelectOption,
          )}
          value={contentStatusFilterValue.toString()}
          onChange={(newValue: string) => {
            setContentStatusFilterValue(newValue as ContentStatusFilterValue);
          }}
          title="Status"
        />
      </div>
      {elements.length === 0 ? (
        <p>Pensumet er tomt.</p>
      ) : (
        filteredElementsList.length === 0 && (
          <p>
            Pensumet har ikke noe innhold med status &quot;
            {contentStatusFilterValueLabel(contentStatusFilterValue)}&quot;.
          </p>
        )
      )}
      {filteredElementsList.length > 0 && (
        <AdministrationList
          showElementIcons
          isInSyllabus={isInSyllabus(context)}
          elements={convertToList(filteredElementsList, data.examSyllabusById)}
          onSaveListOrder={handleSaveListOrder}
          canDelete
          complete={refetch}
        />
      )}

      {studyProgramme?.id && (
        <CreateContentFooterBox
          buttonText="Legg til undervisningsmateriell"
          content={(props) => (
            <ExamSyllabusAddFolder
              {...props}
              complete={() => {
                void refetch();
              }}
              examSyllabusId={examSyllabusId}
              studyProgrammeId={studyProgramme.id}
            />
          )}
          onUpdated={() => {
            void refetch();
          }}
        />
      )}
      <MutationLoadingOrError loading={false} error={error} />
    </AdministrationLayout>
  );
};
